// Chat files (CHAT-FILES): files on chat messages. A chat file belongs to its MESSAGE, never to a
// task: talking to an agent never needs or makes a task. It reuses the task files' storage and
// proxy (`files/{company}/{uuid}`, no presigned URLs) with a message owner:
//
//   attachments.upload   multipart `file` + `thread_id`: stages one file for the caller's next
//                        message in that thread (a thread it may post in). One request per file,
//                        so the composer shows each file's progress. At most the company's cap
//                        (default 25 MB). Staged files are the uploader's alone until sent, and
//                        are dropped after CHAT_FILE_LIMITS.stagedTtlHours.
//   attachments.discard  the ✕ on a staged file (its uploader only; a sent file goes with its
//                        message).
//   messages.create      `attachment_ids` (at most CHAT_FILE_LIMITS.maxPerMessage) binds the
//                        staged files to the new message; a message may be files only.
//   attachments.get      one file (`ChatAttachment`).
//   attachments.content  the bytes through the authenticated proxy (`?thumb=true`, `?download=
//                        true`, byte ranges, as `files.content`).
//   attachments.save     "Save to task…": an independent COPY onto a task the caller may edit
//                        (its own bytes and row, with provenance), so it survives the message,
//                        the thread or the channel being deleted.
//
// Who: exactly whoever can read the message — the thread's rule (domain/chat/places.ts: channel,
// topic, DM, private channel, task or approval thread) while the message is live. Deleting the
// message trashes its files; a deleted message's file is a 404. Every write is an event (the
// audit trail): `attachment.uploaded`, `attachment.attached`, `attachment.removed`,
// `attachment.saved`.
//
// The agent tools (a DE's run token or agent key with `files:read` / `files:write`, and the same
// thread rule):
//   read_chat_attachment    `GET agent/chat-attachments/{id}?maxWidth=`: the bytes as base64 (the
//                           `read_attachment` shape), so a self-hosted DE can write the file into
//                           its own folder; over 5 MB, the hint names `attachments.content`.
//   upload_chat_attachment  `POST agent/chat-attachments` `{threadId,fileName,contentBase64}`:
//                           stages a file; `post_message` with `fileIds` sends it.
//   save_chat_attachment    `POST agent/chat-attachments/{id}/save` `{taskId,folderPath?}`: files
//                           a copy onto a task (a deliverable), with provenance.
import { z } from "zod";
import { Timestamp, text, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import {
  AgentAttachmentContent,
  AgentAttachmentReadQuery,
  FILE_LIMITS,
  FileContentQuery,
  FileItem,
  FilePreview,
  FileUploader,
} from "./files";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const chat = { feature: "chat" } as const;

export const CHAT_FILE_LIMITS = {
  /** Files one message carries at most. */
  maxPerMessage: 20,
  /** Staged (unsent) files one person may hold at once. */
  maxStaged: 60,
  /** A staged file never sent is dropped after this long. */
  stagedTtlHours: 24,
} as const;

/** Why a file has no in-app preview although it is an image (HEIC: browsers do not draw it). */
export const ChatPreviewGap = z.enum(["heic"]);

export const ChatAttachment = z
  .object({
    id: Uuid,
    file_name: z.string(),
    mime: z.string(),
    bytes: z.int().min(1),
    /** How the client may show it (`image`: a preview that opens full size). */
    preview: FilePreview,
    /** Whether `attachments.content?thumb=true` has a thumbnail. */
    has_thumb: z.boolean(),
    /** An image's pixel size (EXIF orientation applied), for a placeholder; null when unknown. */
    width: z.int().min(1).nullable().optional(),
    height: z.int().min(1).nullable().optional(),
    /** An image the app cannot preview (`heic`); null otherwise. Download still works. */
    preview_gap: ChatPreviewGap.nullable(),
    /** The message's thread; null while staged for a topic the message will make. */
    thread_id: Uuid.nullable(),
    /** The message it is on; null while it is staged (not sent yet). */
    message_id: Uuid.nullable(),
    uploader: FileUploader,
    created_at: Timestamp,
  })
  .meta({ id: "ChatAttachment" });
export type ChatAttachment = z.infer<typeof ChatAttachment>;

export const ChatAttachmentUpload = z
  .object({
    file: z.string().describe("The file (multipart part `file`)").meta({ format: "binary" }),
    /**
     * The thread the file will be sent in (the caller must be able to post there); left out when
     * the message will open a new topic (the send checks the thread then).
     */
    thread_id: Uuid.optional(),
  })
  .meta({ id: "ChatAttachmentUpload" });

export const ChatAttachmentSave = z
  .strictObject({
    /** The task to file the copy onto (the caller must be able to edit it). */
    task_id: Uuid,
    /** A `/` folder chain on the task, made as needed. */
    folder_path: z.string().max(512).optional(),
  })
  .meta({ id: "ChatAttachmentSave" });

// Agent tools ------------------------------------------------------------------------------------

export const AgentChatAttachmentUpload = z
  .strictObject({
    threadId: Uuid,
    fileName: text(255),
    contentBase64: z.string().min(1).max(FILE_LIMITS.maxAgentBodyBytes),
    mimeType: z.string().max(255).optional(),
  })
  .meta({ id: "AgentChatAttachmentUpload" });

export const AgentChatAttachmentStaged = z
  .object({
    id: Uuid,
    fileName: z.string(),
    mimeType: z.string(),
    sizeBytes: z.int(),
    threadId: Uuid,
  })
  .meta({ id: "AgentChatAttachmentStaged" });

export const AgentChatAttachmentSave = z
  .strictObject({ taskId: Uuid, folderPath: z.string().max(512).optional() })
  .meta({ id: "AgentChatAttachmentSave" });

export const AgentChatAttachmentSaved = z
  .object({
    /** The copy's id (a task file: `read_attachment` reads it). */
    id: Uuid,
    fileName: z.string(),
    sizeBytes: z.int(),
    taskId: Uuid,
    folder: z.string(),
    /** The chat file it was copied from. */
    sourceId: Uuid,
  })
  .meta({ id: "AgentChatAttachmentSaved" });

export const chatFileRoutes = {
  "attachments.upload": defineRoute({
    ...chat,
    id: "attachments.upload",
    method: "post",
    path: `${base}/chat/attachments`,
    tag: "attachments",
    summary: "Stage one file for the caller's next message in a thread (sent with the message)",
    access: "member",
    params: CompanyParams,
    body: ChatAttachmentUpload,
    bodyType: "multipart",
    idempotency: "optional",
    response: { status: 201, schema: ChatAttachment },
    errors: ["payload_too_large", "scope_missing"],
    emits: ["attachment.uploaded"],
  }),
  "attachments.discard": defineRoute({
    ...chat,
    id: "attachments.discard",
    method: "delete",
    path: `${base}/chat/attachments/{id}`,
    tag: "attachments",
    summary: "Remove a staged (unsent) file of the caller's",
    access: "member",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: ["conflict"],
    emits: ["attachment.removed"],
  }),
  "attachments.get": defineRoute({
    ...chat,
    id: "attachments.get",
    method: "get",
    path: `${base}/chat/attachments/{id}`,
    tag: "attachments",
    summary: "A chat file (whoever can read its message)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ChatAttachment },
    errors: [],
    emits: [],
  }),
  "attachments.content": defineRoute({
    ...chat,
    id: "attachments.content",
    method: "get",
    path: `${base}/chat/attachments/{id}/content`,
    tag: "attachments",
    summary: "A chat file's bytes or thumbnail through the authenticated proxy (byte ranges)",
    access: "member",
    params: CompanyItemParams,
    query: FileContentQuery,
    response: { status: 200, contentType: "application/octet-stream" },
    errors: ["scope_missing"],
    emits: [],
  }),
  "attachments.save": defineRoute({
    ...chat,
    id: "attachments.save",
    method: "post",
    path: `${base}/chat/attachments/{id}/save`,
    tag: "attachments",
    summary: "Save to task: an independent copy of a chat file onto a task, with its provenance",
    access: "member",
    params: CompanyItemParams,
    body: ChatAttachmentSave,
    idempotency: "optional",
    response: { status: 201, schema: FileItem },
    errors: ["payload_too_large", "scope_missing"],
    emits: ["task.file_changed", "task.file_folder_changed", "attachment.saved"],
  }),

  "agentChatFiles.read": defineRoute({
    ...chat,
    id: "agentChatFiles.read",
    method: "get",
    path: `${base}/agent/chat-attachments/{id}`,
    tag: "agent-files",
    summary:
      "read_chat_attachment: a chat file's bytes as base64 (images downscaled; 5 MB cap) (files:read)",
    access: "run_token",
    params: CompanyItemParams,
    query: AgentAttachmentReadQuery,
    response: { status: 200, schema: AgentAttachmentContent },
    errors: ["scope_missing", "payload_too_large"],
    emits: [],
  }),
  "agentChatFiles.upload": defineRoute({
    ...chat,
    id: "agentChatFiles.upload",
    method: "post",
    path: `${base}/agent/chat-attachments`,
    tag: "agent-files",
    summary:
      "upload_chat_attachment: stage a base64 file for a reply in a thread; post_message fileIds sends it (files:write)",
    access: "run_token",
    params: CompanyParams,
    body: AgentChatAttachmentUpload,
    idempotency: "optional",
    response: { status: 201, schema: AgentChatAttachmentStaged },
    errors: ["scope_missing", "payload_too_large"],
    emits: ["attachment.uploaded", "run.step_recorded"],
  }),
  "agentChatFiles.save": defineRoute({
    ...chat,
    id: "agentChatFiles.save",
    method: "post",
    path: `${base}/agent/chat-attachments/{id}/save`,
    tag: "agent-files",
    summary:
      "save_chat_attachment: file a copy of a chat file onto a task, with provenance (files:write)",
    access: "run_token",
    params: CompanyItemParams,
    body: AgentChatAttachmentSave,
    idempotency: "optional",
    response: { status: 201, schema: AgentChatAttachmentSaved },
    errors: ["scope_missing", "payload_too_large"],
    emits: [
      "task.file_changed",
      "task.file_folder_changed",
      "attachment.saved",
      "run.step_recorded",
    ],
  }),
};
